import { useCallback, useEffect, useMemo, useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useNavigate, useMatch } from 'react-router-dom';
import ConversionDialog from 'components/Modals/ConversionDialog';
import { locations } from 'services/routes/locations';
import { ReleaseErrorType } from 'services/api/responses/responseTypes';

interface PropsType {
  error: SerializedError | null;
  onClose: () => void;
}

const DistributionWarningModal = ({ error, onClose }: PropsType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const matchPath = useMatch(locations.general.aiMusicGenerator.pathname);

  const [warningError, setWarningError] = useState(error);

  useEffect(() => {
    if (!error) {
      return;
    }
    setWarningError(error);
  }, [error]);

  const onSubmitClick = useCallback(() => {
    if (!matchPath) {
      navigate(locations.general.aiMusicGenerator.pathname);
    }
    onClose();
  }, [matchPath, onClose, navigate]);

  const description = useMemo(() => {
    if (warningError?.code === ReleaseErrorType.SONG_TOO_SHORT) {
      return t('distribution:releaseWarningModal:description:songToShort');
    }
    if (
      warningError?.code === ReleaseErrorType.CATALOG_SONGS_CANNOT_BE_RELEASED
    ) {
      return t('distribution:releaseWarningModal:description:catalogSong');
    }
    if (warningError?.code === ReleaseErrorType.ONLY_SONG_OWNER_CAN_RELEASE) {
      return t('distribution:releaseWarningModal:description:notTheSongOwner');
    }
    if (warningError?.code === ReleaseErrorType.SONG_ALREADY_IN_RELEASE) {
      return t(
        'distribution:releaseWarningModal:description:songAlreadyReleased'
      );
    }

    return t('messages:error:unknown');
  }, [t, warningError]);

  return (
    <ConversionDialog
      title={t('distribution:releaseWarningModal:title')}
      description={description}
      state={{
        isOpen: !!error,
        error: null,
        isLoading: false,
      }}
      action={{
        onSubmit: onSubmitClick,
        onClose,
        onExited: onClose,
      }}
      submitText={t('distribution:releaseWarningModal:submit')}
      closeText={t('distribution:releaseWarningModal:cancel')}
    />
  );
};

export default DistributionWarningModal;
